import { useQueryClient } from '@tanstack/react-query'
import { DownloadIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import Card from 'apps/employer_renewals/components/card/Card'
import Header from 'apps/employer_renewals/components/Header'
import PageContainer from 'apps/employer_renewals/components/layout/PageContainer'
import { RENEWAL_EVENTS, useEmitEmployerRenewalsEvent } from 'apps/employer_renewals/events'
import useAppNavigation from 'apps/employer_renewals/hooks/useAppNavigation'
import { useEmployerRenewalConfig } from 'apps/employer_renewals/providers/EmployerRenewalConfigProvider'
import { EmployerRenewalsRoute } from 'apps/employer_renewals/routes'
import { Badge, Button, ButtonLink } from 'slab'
import {
  getGetEmployersRenewalsRenewalConfigsIdQueryKey as getRenewalConfigQueryKey,
  getGetQuotingProspectsProspectIdCensusIdQueryKey as getCensusQueryKey,
  patchEmployersRenewalsRenewalConfigsIdUploadEmployeeCensusUpdates as useUpdateRenewalConfigsEmployeeCensusUpdates,
  useGetQuotingProspectsProspectIdCensusId as useGetCensus
} from 'utils/web_api'

import CensusUpload from './CensusUpload'
import StepsLayout from './steps/StepsLayout'

export default function CensusPage () {
  const { navigateTo } = useAppNavigation()
  const { emitEvent } = useEmitEmployerRenewalsEvent()

  const queryClient = useQueryClient()
  const { employerRenewalConfig } = useEmployerRenewalConfig()
  const [isUploading, setIsUploading] = useState(false)
  const [isAwaitingCensusToProcess, setIsAwaitingCensusToProcess] = useState(false)

  const { data: census, isLoading: censusIsLoading } = useGetCensus(
    employerRenewalConfig?.prospect_id ?? '',
    employerRenewalConfig?.employee_census_updates_id ?? '',
    {
      query: {
        enabled: !!employerRenewalConfig?.employee_census_updates_id && !!employerRenewalConfig?.prospect_id,
        retry: false,
        refetchInterval: (query) => {
          if (['processed', 'failed'].includes(query.state.data?.status ?? '')) {
            return false
          } else if (query.state.data?.status === 'processing') {
            // This is used to determine if we should auto-redirect upon a census becoming processed
            // if the census is in a processing state here, we assume they uploaded the census on this or a recent page view and should automatically redirect
            setIsAwaitingCensusToProcess(true)
          }
          return 1_000
        },
      }
    }
  )

  const handleFileUpload = async (file: File) => {
    if (!employerRenewalConfig?.id || !employerRenewalConfig?.prospect_id) {
      return
    }

    const formData = new FormData()
    formData.append('file', file)

    setIsUploading(true)

    try {
      const response = await useUpdateRenewalConfigsEmployeeCensusUpdates(
        employerRenewalConfig.id,
        { body: formData }
      )

      const censusQueryKey = getCensusQueryKey(employerRenewalConfig.prospect_id, response.id)
      queryClient.setQueryData(censusQueryKey, response)

      const renewalConfigQueryKey = getRenewalConfigQueryKey(employerRenewalConfig.id)
      queryClient.setQueryData(renewalConfigQueryKey, (oldConfig: any) => ({
        ...oldConfig,
        employee_census_updates_id: response.id
      }))
    } catch (error) {
      console.error('Upload error:', error)
      toast.error('An error occurred while uploading your employee information. Please try again or contact support if the problem persists.')
    } finally {
      setIsUploading(false)
    }
  }

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>
    if (census?.status === 'processed' && isAwaitingCensusToProcess) {
      timeout = setTimeout(() => {
        navigateTo(EmployerRenewalsRoute.EMPLOYEES_ELIGIBILITY)
      }, 1_000)
    }

    if (census?.status === 'failed') {
      emitEvent(RENEWAL_EVENTS.CENSUS_FAILED, {
        parsing_errors: census.parsing_errors?.join(', '),
      })
    }

    return () => clearTimeout(timeout)
  }, [census?.status, navigateTo])

  return (
    <PageContainer>
      <Header
        heading='Confirm your employee information'
        subheading='To best estimate your 2026 rates, we need to make sure we have the correct information about your team.'
      />
      <StepsLayout steps={[
        <Card key='download-employee-list' raw>
          <div className='flex flex-col gap-3 p-4'>
            <div className='body'>
              First, download your employee list.
            </div>
            <div>
              <ButtonLink
                href={`/employers/prospects/${employerRenewalConfig?.prospect_id}/censuses/${employerRenewalConfig?.employee_census_id}/download`}
                variant='primary'
                disabled={!employerRenewalConfig?.prospect_id || !employerRenewalConfig?.employee_census_id}
                leftIcon={DownloadIcon}
                size='large'
                target='_blank'
              >
                Download your employee list
              </ButtonLink>
            </div>
            <div className='body border-t border-gray-200 pt-4'>
              Or if you'd rather update your employee information from the UI
            </div>
            <div>
              <Button
                onClick={() => { navigateTo(EmployerRenewalsRoute.EMPLOYEES_ELIGIBILITY) }}
                variant='secondary'
                size='large'
              >
                Skip to employee eligibility page
              </Button>
            </div>
          </div>
        </Card>,
        <Card key='fill-in-missing-information' raw>
          <div className='flex flex-col gap-4 p-4 body'>
            <p>Open the file and fill in any missing information. If you have hourly workers, use the formula calculate their annual salary: </p>
            <div>
              <Badge>
                <div className='body-bold text-wrap'>
                  Hourly Wage <span className='text-thatch-500'>x</span> Hours Per Week <span className='text-thatch-500'>x</span> 52 <span className='text-thatch-500'>=</span> Annual Salary
                </div>
              </Badge>
            </div>
            <p>When you're done, save or export the file <span className='body-bold'>in .CSV format</span>.</p>
          </div>
        </Card>,
        <CensusUpload
          key='upload-employee-list'
          census={census}
          censusIsLoading={censusIsLoading}
          isUploading={isUploading}
          onFileUpload={handleFileUpload}
          showSuccessIconWhenCensusIsProcessed={isAwaitingCensusToProcess}
        />
      ]}
      />
    </PageContainer>
  )
}
